import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { UserService } from '../services/user.service';
import { AuthenticationResult, Configuration, PublicClientApplication } from '@azure/msal-browser';
import { AppIdCodes } from '../models/Login/AppIdCodes';
import { environment } from '../../environments/environment';
import { PostLoginNavigationService } from '../services/post.login.navigation.service';
import { SnackBarService } from '../services/snackBar.service';
import { MatSnackBarConfig } from '@angular/material/snack-bar';
import { CookieService } from '../services/cookie.service';

@Injectable({ providedIn: 'root' })
export class MsalStartupService {
    constructor(
        private readonly msalService: MsalService,
        private readonly userService: UserService,
        private readonly postLoginNavigationService: PostLoginNavigationService,
        private readonly snackBarService: SnackBarService,
        private readonly cookieService: CookieService
    ) {}

    error: string | null = null;

    private readonly loginSnackbar: MatSnackBarConfig = {
        duration: 5000,
        verticalPosition: 'top'
    };

    async processRedirectOnStartup(): Promise<void> {
        try {

            const configStr = sessionStorage.getItem('msalConfig');
            const config = configStr ? JSON.parse(configStr) : null;

            // MSAL config not found, first initialization
            if (!config) return;

            const msalConfig: Configuration = {
                auth: {
                    clientId: config.clientId,
                    authority: config.authority,
                    redirectUri: config.redirectUri
                }
            };

            this.msalService.instance = new PublicClientApplication(msalConfig);
            await this.msalService.instance.initialize();
                       
            this.handleMsalRedirect();

        } catch (error) {
            console.error('MSAL startup initialization failed:', error);
            this.userService.setEntraAuthenticated(false);
        }
    }

    private handleMsalRedirect(): void {
        
        this.msalService.handleRedirectObservable().subscribe({
            next: (response: AuthenticationResult) => {

                if (response && response.account) {
                    
                    let customScope: string = '';
                    let rSysId: string = '0';
                    let returnUrl:string = '/';
                    let appId: AppIdCodes = AppIdCodes.Sphinnx;
                    if (returnUrl?.includes(environment.rmsIntegration.domain)) {
                        appId = AppIdCodes.RMS;
                    };
                    let userIdSuffix: string = '';

                    // Pull state sent during the login request
                    if (response.state) {
                        try {
                            const customState = JSON.parse(response.state);

                            appId = customState.appId;
                            customScope = customState.CustomScope || '';
                            rSysId = customState.RSysId || '0';
                            returnUrl = customState.ReturnUrl || '/';
                            userIdSuffix = customState.UserIdSuffix || '';

                        } catch (e) {
                            console.error('Could not parse state.', e);
                        }
                        
                        // Acquire access token using custom scope, use this token to call backend to complete login
                        this.msalService.instance.acquireTokenSilent({
                            scopes: customScope ? [customScope] : [],
                            account: response.account
                        }).then((tokenResult: AuthenticationResult) => {

                            const accessToken = tokenResult.accessToken;

                            // Also retrieving the authority and clientId to complete the login with the backend 
                            // (see ValidateTokenAsync in the EntraTokenValidationService in the API)
                            const { auth } = this.msalService.instance.getConfiguration();

                            const clientId = auth.clientId;
                            const authority = auth.authority ?? '';

                            this.userService.loginEntra(clientId, authority, accessToken, response.accessToken, appId, rSysId, userIdSuffix).subscribe({
                                next: (data) => {
                                    if (data?.token) {
                                        this.userService.setEntraAuthenticated(true);

                                        // Login successful, redirect
                                        this.postLoginNavigationService.navigateAfterLogin(returnUrl);

                                    } else {
                                        // Login unsuccessful
                                        this.error = 'Entra login unsuccessful.';
                                        this.snackBarService.openSnackbar(
                                            "Unable to complete login. Please contact support.",
                                            "OK",
                                            {
                                                ...this.loginSnackbar,
                                                panelClass: ['error-snackbar']
                                            }
                                        );

                                        this.cookieService.clearMsalSessionStorage();
                                        this.userService.setEntraAuthenticated(false);
                                    }
                                },
                                error: (error) => {
                                    console.error('Call to userService.loginEntra failed:', error);
                                    this.error = 'Login failed. Please try again.';
                                    this.snackBarService.openSnackbar(
                                        "Unable to complete login. Please contact support.",
                                        "OK",
                                        {
                                            ...this.loginSnackbar,
                                            panelClass: ['error-snackbar']
                                        }
                                    );

                                    this.cookieService.clearMsalSessionStorage();
                                }
                            });                            

                        }).catch((error) => {
                            console.error('Token acquisition failed:', error);
                            this.error = 'Failed to acquire access token.';
                            this.cookieService.clearMsalSessionStorage();
                            this.userService.setEntraAuthenticated(false);
                        });

                    }

                }
            },
            error: (error) => {
                // Handle errors during the redirect process
                console.error('Redirect error:', error);
                this.cookieService.clearMsalSessionStorage();
                this.userService.setEntraAuthenticated(false);
            }
        });

    }


}